<template>
  <Loader v-if="loading" />

  <div class="page-container">
    <div class="flex align-items-center justify-content-between mb-4">
      <div>
        <h1 class="m-0 text-3xl font-semibold text-primary"><i class="pi pi-wallet mr-2"></i>Transazioni Bancarie</h1>
        <p class="text-color-secondary mt-1 mb-0">Gestisci e importa le tue transazioni da file CSV bancario</p>
      </div>
    </div>

    <div class="grid mb-4">
      <!-- Upload CSV -->
      <div class="col-12 md:col-6">
        <Card class="h-full border-1 border-200 shadow-2 hover:shadow-4 transition-all transition-duration-200">
          <template #title>
            <div class="flex align-items-center gap-2 text-primary">
                <i class="pi pi-cloud-upload text-xl"></i>
                <h3 class="m-0 text-lg">Carica file dalla banca</h3>
            </div>
          </template>
          <template #content>
            <div class="flex flex-column gap-3">
                <div class="flex align-items-center">
                    <Checkbox v-model="excludeUscite" inputId="excludeUscite" :binary="true" />
                    <label for="excludeUscite" class="ml-2 font-medium">Non importare uscite (segno negativo)</label>
                </div>
                <div 
                  class="upload-area border-2 border-dashed border-primary border-round-xl p-4 text-center cursor-pointer transition-colors transition-duration-150 hover:surface-hover flex flex-column align-items-center justify-content-center" 
                  style="min-height: 140px;"
                  @click="triggerFileInput"
                >
                  <i class="pi pi-file-excel text-4xl text-primary mb-2"></i>
                  <span class="font-medium text-color text-lg">Clicca qui per sfogliare</span>
                  <span class="text-color-secondary text-sm mt-1">Supporta formati CSV, XLS, XLSX</span>
                  <input type="file" ref="fileInput" @change="onFileChange" class="hidden" accept=".csv, .xls, .xlsx" style="display: none;" />
                </div>
            </div>
          </template>
        </Card>
      </div>

      <!-- Filtri e Switch Vista -->
      <div class="col-12 md:col-6">
        <Card class="h-full border-1 border-200 shadow-2">
          <template #title>
             <div class="flex align-items-center gap-2 text-primary">
                <i class="pi pi-filter text-xl"></i>
                <h3 class="m-0 text-lg">Filtri e Visualizzazione</h3>
            </div>
          </template>
          <template #content>
            <div class="flex flex-column gap-4 justify-content-center h-full pt-2">
                <Dropdown
                    v-model="selectedActivity"
                    :options="activities"
                    optionLabel="nome"
                    optionValue="id"
                    placeholder="Filtra per Attività"
                    class="w-full p-inputtext-lg"
                    :showClear="true"
                />
                <Button
                  :class="['w-full p-button-lg font-bold p-button-outlined shadow-2', showAll ? 'p-button-danger' : 'p-button-primary']"
                  :icon="showAll ? 'pi pi-list' : 'pi pi-clock'"
                  @click="changeData"
                >
                  <span class="ml-2">Mostra {{ showAllText }}</span>
                </Button>
            </div>
          </template>
        </Card>
      </div>
    </div>

    <!-- Dialog Anteprima -->
    <Dialog v-model:visible="previewVisible" header="Anteprima Importazione" :style="{ width: '80vw' }" :modal="true" :closable="false">
        <p class="mb-3">Di seguito l'anteprima delle righe lette. Le righe evidenziate in verde (con spunta) sono quelle che verranno importate in quanto successive all'ultimo import. Le altre saranno ignorate.</p>
        <DataTable :value="previewData" responsiveLayout="scroll" class="p-datatable-sm p-datatable-gridlines">
            <Column field="is_new" header="Importa" style="width: 80px; text-align: center;">
                <template #body="slotProps">
                    <i v-if="slotProps.data.is_new" class="pi pi-check text-green-500 font-bold"></i>
                    <i v-else class="pi pi-times text-red-500"></i>
                </template>
            </Column>
            <Column field="data_valuta" header="Data Valuta" />
            <Column field="data_contabile" header="Data Contabile" />
            <Column field="importo" header="Importo" style="text-align: right;" />
            <Column field="descrizione" header="Descrizione" />
        </DataTable>
        <template #footer>
            <Button label="Annulla" icon="pi pi-times" @click="annullaImport" class="p-button-text" />
            <Button label="Conferma Importazione" icon="pi pi-check" @click="confermaImport" autofocus />
        </template>
    </Dialog>

    <!-- Movimenti contabilizzati -->
    <Card v-if="showAll" class="mb-4 border-1 border-200 shadow-2">
      <template #title>
        <div class="flex align-items-center gap-2">
            <i class="pi pi-check-circle text-green-500 text-xl"></i>
            <h3 class="m-0 text-lg">{{ quanti }} Movimenti contabilizzati</h3>
        </div>
      </template>
      <template #content>
        <DataTable
          :value="transactions"
          responsiveLayout="scroll"
          v-if="quanti > 0"
          class="p-datatable-striped"
        >
          <Column field="data_valuta" header="Data Valuta" />
          <Column field="data_contabile" header="Data Contabile" />
          <Column field="importo" header="Importo" style="text-align: right;" />
          <Column field="descrizione" header="Descrizione" />
          <Column header="Dettaglio">
            <template #body="slotProps">
              <Button
                class="p-button-info p-button-sm"
                @click="goToAtleta(slotProps.data.id_atleta)"
              >
                {{ slotProps.data.atleta }}
              </Button>
            </template>
          </Column>
        </DataTable>

        <Paginator
            v-if="quanti > 0"
            :rows="limitUsed"
            :totalRecords="totalUsed"
            :first="(pageUsed - 1) * limitUsed"
            @page="onPageChangeUsed"
            class="mt-3"
        />
        <div v-else class="text-center p-3">
          <p v-if="loading">Caricamento in corso...</p>
          <p v-else>Al momento non ci sono movimenti contabilizzati</p>
        </div>
      </template>
    </Card>

    <!-- Movimenti da contabilizzare -->
    <Card v-if="!showAll" class="mb-4 border-1 border-200 shadow-2">
      <template #title v-if="!loading">
        <div class="flex align-items-center gap-2">
            <i class="pi pi-clock text-orange-500 text-xl"></i>
            <h3 class="m-0 text-lg">{{ quantiNo }} Movimenti da contabilizzare</h3>
        </div>
      </template>
      <template #content>
        <DataTable
          :value="transactionsNotUsed"
          responsiveLayout="scroll"
          v-if="quantiNo > 0 && !loading"
          class="p-datatable-striped"
        >
          <Column field="data_valuta" header="Data Valuta" />
          <Column field="data_contabile" header="Data Contabile" />

          <Column field="importo" header="Importo" style="text-align: right;" />
          <!-- Descrizione con evidenziazione -->
          <Column field="descrizione" header="Descrizione">
            <template #body="slotProps">
              <span>
                {{ slotProps.data.descrizione }}
                <template
                  v-for="match in highlightNames(slotProps.data.descrizione, atleti)"
                 :key="Number(match.id)"
                >
                  <Button
                    class="p-button-success p-button-sm ml-2"
                    @click="openConfirmDialogUsed(match.id, slotProps.data.id, slotProps.data)"
                  >
                    {{ match.text }}
                  </Button>
                </template>
              </span>
            </template>
          </Column>

          <!-- Azioni -->
          <Column header="Azioni">
            <template #body="{ data }">
              <div class="flex gap-2">
                <Button
                  v-if="!showAll"
                  label="Contabilizza"
                  class="p-button-sm p-button-success"
                  @click="openConfirmDialogUsed(null, data.id, data)"
                />
                <Button
                  v-if="!showAll"
                  label="Cancella"
                  class="p-button-sm p-button-warning"
                  @click="confermaCancellazione(data.id)"
                />
              </div>
            </template>
          </Column>
        </DataTable>

        <Paginator
            v-if="quantiNo > 0"
            :rows="limitNoUsed"
            :totalRecords="totalNoUsed"
            :first="(pageNoUsed - 1) * limitNoUsed"
            @page="onPageChangeNoUsed"
            class="mt-3"
        />
        <div v-else class="text-center p-3">
          <p v-if="loading">Caricamento in corso...</p>
          <p v-else>Nessun movimento da contabilizzare</p>
        </div>
      </template>
      <ConfirmDialog />
    </Card>

    <!-- Modal contabilizzazione -->
    <div v-if="showModal" class="modal">
      <h3>📝 Contabilizza Transazione</h3>

      <div class="mb-3 p-3 border-round surface-100">
        <h4>Riepilogo Transazione</h4>
        <p><strong>Descrizione:</strong> {{ datiTransazione?.descrizione || 'N/A' }}</p>
        <p><strong>Importo:</strong> € {{ datiTransazione?.importo?.toFixed(2) || '0.00' }}</p>
      </div>

      <!-- Selezione Atleta -->
      <div class="flex align-items-center gap-3 mt-3">
        <label for="atleta" class="font-semibold">Seleziona Atleta:</label>
        <Dropdown
          v-model="selectedAtleta"
          :options="atleti"
          :filter="true"
          optionLabel="nomecompleto"
          optionValue="id"
          placeholder="Seleziona atleta"
          class="w-full"
          :virtualScrollerOptions="{ itemSize: 10 }"
          @change="changeAtleta($event)"
        />
        <Button
          v-if="selectedAtleta"
          class="p-button-primary"
          :label="`Vai a ${getNomeCompleto(selectedAtleta)}`"
          icon="pi pi-user"
          @click="goToAtleta(selectedAtleta)"
        />
      </div>

      <!-- Tabella pagamenti -->
      <DataTable
        :value="dapagare"
        class="p-datatable-gridlines mt-3"
        v-model:selection="pagamento.richieste_pagamenti"
        :metaKeySelection="false"
        selectionMode="multiple"
        editMode="cell"
        @rowSelect="onRowSelect"
        @rowUnselect="onRowSelect"
      >
        <Column field="corso" header="Descrizione">
          <template #body="{ data }">
            {{ data.n_rata ? 'Rata n. ' + data.n_rata + ' del corso ' + data.corso : '' }}
            {{ data.kit ? 'Kit ' + data.kit : '' }}
            {{ data.prodotto ? data.prodotto : '' }}
          </template>
        </Column>

        <Column field="data_scadenza" header="Data Scadenza" />

        <Column field="importo" header="Importo €" style="text-align: right">
          <template #body="{ data }">
            {{ data.importo.toFixed(2) }}
          </template>
          <template #editor="{ data, column }">
            <InputNumber
              v-model="data[column.props.field]"
              mode="currency"
              currency="EUR"
              locale="it-IT"
              autofocus
              fluid
            />
          </template>
        </Column>

        <template #footer v-if="viewSetPagamento">
          <div class="flex justify-content-between align-items-center">
            <p class="font-bold">Totale: € {{ pagamento.totale.toFixed(2) }}</p>
            <div class="flex gap-2">
              <Button
                v-if="canSendReceipt"
                class="p-button-info"
                icon="pi pi-envelope"
                label="Salva e Invia Ricevuta"
                @click="saveAndSendReceipt"
              />
              <Button
                v-if="showCompleteButton"
                class="p-button-success"
                icon="pi pi-check"
                label="Completa pagamento"
                @click="sendPagamento"
              />
             
            </div>
          </div>
        </template>
      </DataTable>

      <!-- Azioni modal -->
      <div class="mt-4 flex justify-content-end gap-2">
        <Button
          @click="confirmDialogRejectUsed"
          label="Annulla"
          class="p-button-secondary"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.5rem;
}
.upload-area {
    background-color: var(--surface-50);
}
.upload-area:hover {
    background-color: var(--surface-100);
    border-color: var(--primary-color) !important;
}
.modal {
  background: #fff;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>


<script>
import {ref,onMounted, getCurrentInstance,computed, watch } from 'vue';
import Service from '@/services/levante-api/levante-api'
import Loader from "@/components/Loader.vue";
import { useToast } from 'primevue/usetoast';
import moment from 'moment'

export default {
  components: {
    Loader,
  },
  
    setup(){
        const self = getCurrentInstance();
        self.$eventBus = self.appContext.config.globalProperties.$eventBus
        const service = new Service(self.$eventBus);
        const toast = useToast();
        const showAllText = ref("contabilizzati")
        const showAllTextAlt = ref("da contabilizzare")
        const transactions = ref([])
        const transactionsNotUsed = ref([])
        const atleti = ref({})
        const dapagare = ref({})
        const quanti = ref(0)
        const quantiNo = ref(0)
        const showModal = ref(false)
        const datiTransazione = ref(null);

        const previewVisible = ref(false);
        const previewData = ref([]);
        const selectedFileFormData = ref(null);
        const fileInput = ref(null);
        const excludeUscite = ref(true);

        const showAll = ref(false)
        const selectedTransaction = ref()
        const selectedAtleta =ref()
        const loading = ref(false)
        const lifeTime = 3000;
        const pagamento = ref({
            'idstripe':null,
            'richieste_pagamenti': [],
            'type_pay': 2,
            'totale': 0,
            'id_atleta': null,
            'id_transaction': null,
            "data": null,
        })
        const confirmDialogUsed = ref({
            visible: false,
            id: null,
            visibile: null,
            selectedCorso: null
        });

        // Pagination & Filtering
        const activities = ref([]);
        const selectedActivity = ref(null);
        
        const pageUsed = ref(1);
        const limitUsed = ref(50);
        const totalUsed = ref(0);

        const pageNoUsed = ref(1);
        const limitNoUsed = ref(50);
        const totalNoUsed = ref(0);

        const goToAtleta = (idatleta)=> {
            window.open(`/atleta/${idatleta}/dettagli`)
            showModal.value=false;
            dapagare.value=[];
            pagamento.value.id_atleta = null
        }
        const changeData = () => {
            console.log("changeData")
            showAll.value=!showAll.value
            if (showAll.value){
                showAllText.value="da contabilizzare";
                showAllTextAlt.value="contabilizzati"
            }else{
                showAllText.value="contabilizzati"
                showAllTextAlt.value="da contabilizzare"
            }
            fetchData()
        }
        const getNomeCompleto = (id) => {
            const atleta = atleti.value.find(a => a.id === id);
            return atleta ? atleta.nome+' '+atleta.cognome : '';
        };
        const openConfirmDialogUsed = async (id, idstripe,dati) => {
            pagamento.value.idstripe = idstripe
            pagamento.value.id_transaction = idstripe;
            pagamento.value.totale = 0;
            pagamento.value.richieste_pagamenti = [];
            pagamento.value.id_atleta = null
            pagamento.value.data=moment(dati.data_valuta).format('DD/MM/YYYY')
            selectedAtleta.value=id
            datiTransazione.value = dati;
            dapagare.value=[]
            if (id){
                pagamento.value.id_atleta = id
                await service.getDaPagare('atleta', id)
                .then((res) => {
                dapagare.value=res.items
                
                showModal.value=true
                //document.body.style.overflow = 'hidden';
                });
            }else{
                console.log("ciao");
                pagamento.value.id_atleta = null
                showModal.value=true
                //document.body.style.overflow = '';
            }
        };

        const saveAndSendReceipt = () => {
             //loading.value = true
            let fd = new FormData();
            fd.append('pagamento', JSON.stringify(pagamento.value));
            showModal.value = false;
            purgeDataFromTransaction(pagamento.value.idstripe)
            service.sendPagamento(fd)
            .then(async (res) => {
                
                let severity = res.item.esito
                let summary = res.item.esito != 'success' ? 'Attenzione' : 'Successo'
                let detail = res.item.messaggio
                
                if (res.item.esito=='success'){
                    // Check for email and send receipt
                    if (pagamento.value.id_atleta) {
                        const atleta = atleti.value.find(a => a.id === pagamento.value.id_atleta);
                        // Assuming we want to send receipt if payment is successful
                         // Call sendRicevuta logic similar to AtletiDetail
                         // We need the payment ID returned from sendPagamento
                         if (res.item.dati && res.item.dati.id_pagamento) {
                             await service.sendRicevuta(res.item.dati.id_pagamento, new FormData())
                             .then((resRicevuta) => {
                                 toast.add({severity: resRicevuta.item.esito, summary: 'Ricevuta', detail: resRicevuta.item.messaggio, life: lifeTime});
                             });
                         }
                    }

                    service.setBancaUsed(fd,pagamento.value.idstripe).then((res)=>{
                        //fetchData();
                    })
                }
                
                if (severity != 'warning') {
                    toast.add({severity:severity, summary: summary, detail:detail,life:lifeTime});  
                }
            }).finally(()=>{
                loading.value = false
            })
        }

        const sendPagamento = (id) => {
            //loading.value = true
            let fd = new FormData();
            fd.append('pagamento', JSON.stringify(pagamento.value));
            showModal.value = false;
            purgeDataFromTransaction(pagamento.value.idstripe)
            service.sendPagamento(fd)
            .then((res) => {
                
                let severity = res.item.esito
                let summary = res.item.esito != 'success' ? 'Attenzione' : 'Successo'
                let detail = res.item.messaggio
                if (res.item.esito=='success'){
                    service.setBancaUsed(fd,pagamento.value.idstripe).then((res)=>{
                        
                        //fetchData();
                       
                    })
                }
                
                if (severity != 'warning') {
                    toast.add({severity:severity, summary: summary, detail:detail,life:lifeTime});  
                                       
                }
            }).finally(()=>{
                loading.value = false
            })
        }
        const purgeDataFromTransaction = (idstripe) => {
            console.log("purgeDataFromTransaction", idstripe);
            console.log("transactionsNotUsed before purge:", transactionsNotUsed.value.length);

            if (!Array.isArray(transactionsNotUsed.value)) {
                transactionsNotUsed.value = []; // fallback per sicurezza
            }

            transactionsNotUsed.value = transactionsNotUsed.value.filter(
                t => t.id !== idstripe
            );

            quantiNo.value = transactionsNotUsed.value.length;
            console.log("transactionsNotUsed after purge:", transactionsNotUsed.value.length);
        };
        const showCompleteButton = computed(() => {
            if(!datiTransazione.value?.importo) return false;
            
            const totale = Number(pagamento.value.totale.toFixed(2));
            const importo = Number(datiTransazione.value.importo.toFixed(2));
            
            return totale === importo;
        });

        const canSendReceipt = computed(() => {
            if (!showCompleteButton.value) return false;
            if (!pagamento.value.id_atleta) return false;
            const athlete = atleti.value.find(a => a.id === pagamento.value.id_atleta);
            console.log("athlete", athlete);
            return athlete && athlete.email && athlete.email.includes('@');
        });

        // Modifica la funzione di calcolo totale per maggiore precisione
        const setTotaleVendita = () => {
            let totale = 0;
            pagamento.value.richieste_pagamenti.forEach(prodotto => {
                totale += Number(prodotto.importo.toFixed(2));
            });
            pagamento.value.totale = totale;
        };
        const onCellEditComplete = () => {
            setTotaleVendita();
        };
        const onRowSelect = (event) => {
            setTotaleVendita()
            if (!Object.keys(pagamento.value.richieste_pagamenti).length) {
                pagamento.value.type_pay = 2
                
            }
        }
        const highlightNames = (description, atleti) => {
            if (showAll.value || !description) return [];

            const matches = [];
            const desc = description.replace(/\s+/g, ' ').toLowerCase();

            for (const atleta of atleti) {
                if (!atleta) continue;

                // nome completo
                if (atleta.nomecompleto) {
                    const nomeCompleto = atleta.nomecompleto.trim();
                    if (desc.includes(nomeCompleto.toLowerCase())) {
                        matches.push({ text: nomeCompleto, id: atleta.id });
                    }
                }

                // nome + cognome e cognome + nome
                if (atleta.nome && atleta.cognome) {
                    const nome = atleta.nome.trim();
                    const cognome = atleta.cognome.trim();
                    const nomeCognome = `${nome} ${cognome}`;
                    const cognomeNome = `${cognome} ${nome}`;

                    const nomeCognomeLower = nomeCognome.toLowerCase();
                    const cognomeNomeLower = cognomeNome.toLowerCase();

                    if (desc.includes(nomeCognomeLower)) {
                        matches.push({ text: nomeCognome, id: atleta.id });
                    }
                    if (desc.includes(cognomeNomeLower)) {
                        matches.push({ text: cognomeNome, id: atleta.id });
                    }
                }
            }

            return matches;
        };

        const viewSetPagamento = computed(()=>{
            return Object.keys(pagamento.value.richieste_pagamenti).length
        })
        
        const fetchData = async() => {
            transactionsNotUsed.value=[]
            transactions.value=[]
            loading.value = true
            
            if (showAll.value){
                // Accounted transactions
                const params = {
                    page: pageUsed.value,
                    limit: limitUsed.value,
                    attivita_id: selectedActivity.value
                };
                
                // Construct query string manually or pass object if service supports it
                // Assuming service methods need update or we pass query params
                // But service.getAllTransactionsUsed() likely just calls axios.get(url)
                // We need to modify service call or pass params
                // Let's assume we can pass params to the service method if we updated it, 
                // but we didn't update service method signature in levante-api.js yet?
                // Wait, I didn't update levante-api.js getAllTransactionsUsed signature.
                // I should check if I can pass query params.
                // levante-api.js: async getAllTransactionsUsed() { const url = ...; return await this.getPostByUrl(url) }
                // It uses getPostByUrl which is likely GET? No, getPostByUrl usually implies POST or GET?
                // Let's check getPostByUrl in levante-api.js.
                // If I can't pass params, I need to update levante-api.js too.
                
                // For now, let's assume I'll update levante-api.js to accept params.
                // Or I can append to URL if I had access.
                
                // Let's update levante-api.js later if needed. 
                // Actually, I should have checked levante-api.js for getAllTransactionsUsed.
                
                // Let's try to pass params as second argument if supported, or update levante-api.js.
                // I'll assume I need to update levante-api.js to support params.
                
                // For now, I'll write the code as if the service accepts params.
                
                service.getAllTransactionsUsed(params).then((res) => {
                    console.log("getAllTransactionsUsed", res);
                    if (res.items && res.items.data) {
                         transactions.value = res.items.data.map(t => ({
                            ...t,
                            id: Number(t.id) // id might be id_transaction or id from join
                        }));
                        totalUsed.value = res.items.total;
                        quanti.value = res.items.total;
                    } else {
                        // Fallback for old API response if not yet updated or different format
                        transactions.value = Object.values(res.items).map(t => ({
                            ...t,
                            id: Number(t.id)
                        }));
                        quanti.value = transactions.value.length;
                    }
                    transactionsNotUsed.value=[]
                    quantiNo.value=0
                }).finally(()=>{
                    loading.value = false;
                });
                
            }else{
                // Unaccounted transactions
                 const params = {
                    page: pageNoUsed.value,
                    limit: limitNoUsed.value
                };
                
                service.getAllTransactionsNoUsed(params).then((response)=>{
                    console.log("getAllTransactionsNoUsed", response);
                     if (response.items && response.items.data) {
                        transactionsNotUsed.value = response.items.data.map(t => ({
                            ...t,
                            id: Number(t.id)
                        }));
                        totalNoUsed.value = response.items.total;
                        quantiNo.value = response.items.total;
                    } else {
                         transactionsNotUsed.value =  Object.values(response.items).map(t => ({
                            ...t,
                            id: Number(t.id)
                        }));
                        quantiNo.value = transactionsNotUsed.value.length;
                    }
                    quanti.value=0
                }).finally(()=>{
                    loading.value = false;
                });
                
            }
        };

        const onPageChangeUsed = (event) => {
            pageUsed.value = event.page + 1;
            limitUsed.value = event.rows;
            fetchData();
        };

        const onPageChangeNoUsed = (event) => {
            pageNoUsed.value = event.page + 1;
            limitNoUsed.value = event.rows;
            fetchData();
        };

        const confirmDialogRejectUsed = () =>{
            showModal.value=false;
            dapagare.value=[];
            pagamento.value.id_atleta = null

        }
     
        const fetchAtleti = async() => {
    
            service.getAtleti().then((res) => {
                console.log(res);
                atleti.value = res.items;
                console.log(atleti.value.length);

                fetchData()

            });
        };
        
        const fetchActivities = async () => {
             service.getActiveCorsi().then((res) => {
                 if (res.items) {
                     activities.value = res.items;
                 }
             });
        };
        
        const { proxy } = getCurrentInstance(); // Per accedere a this.$confirm

        const confermaCancellazione = (id) => {
            proxy.$confirm.require({
                message: 'Sei sicuro di voler cancellare questa transazione?',
                header: 'Conferma',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Sì',
                rejectLabel: 'No',
                accept: () => {
                cancella(id);
                }
            });
        };

        const cancella = async(transaction)=> {
            let formData = new FormData();
            formData.append('transaction', transaction);

            service.delTransaction(formData).then((res) => {
                console.log("res delTransaction");
                console.log(res);
                
                let severity = res.item.esito
                let summary = res.item.esito != 'success' ? 'Attenzione' : 'Successo'
                let detail = res.item.messaggio
                if (severity != 'warning') {
                    toast.add({severity:severity, summary: summary, detail:detail,life:lifeTime});  
                                       
                }
                fetchData();
            });
            
            console.log("res delTransaction end");
        };
        const onFileChange = async (event) => {
            const file = event.target.files[0];
            if (!file) return;
            loading.value = true;
            let formData = new FormData();
            formData.append('file', file);
            formData.append('exclude_uscite', excludeUscite.value);
            selectedFileFormData.value = formData;

            service.previewTransaction(formData).then((res) => {
                if (res.item.esito === 'success') {
                    previewData.value = res.item.data;
                    previewVisible.value = true;
                } else {
                    toast.add({severity: res.item.esito, summary: 'Attenzione', detail: res.item.messaggio, life: lifeTime});
                }
            }).finally(() => {
                loading.value = false;
            });
        };

        const annullaImport = () => {
            previewVisible.value = false;
            previewData.value = [];
            selectedFileFormData.value = null;
            if (fileInput.value) {
                fileInput.value.value = "";
            }
        };

        const triggerFileInput = () => {
            if (fileInput.value) {
                fileInput.value.click();
            }
        };

        const confermaImport = async () => {
            if (!selectedFileFormData.value) return;
            loading.value = true;
            previewVisible.value = false;
            
            selectedFileFormData.value.set('exclude_uscite', excludeUscite.value);

            service.importTransaction(selectedFileFormData.value).then((res) => {
                let severity = res.item.esito
                let summary = res.item.esito != 'success' ? 'Attenzione' : 'Successo'
                let detail = res.item.messaggio
                if (severity != 'warning') {
                    toast.add({severity:severity, summary: summary, detail:detail,life:lifeTime});                            
                }
                fetchData();
                annullaImport();
            }).finally(() => {
                loading.value = false;
            });
        };
        const changeAtleta = async(event) => {
            console.log("changeAtleta");
            console.log(event)
            await service.getDaPagare('atleta',event.value)
                .then((res) => {
                    pagamento.value.id_atleta = event.value
                dapagare.value=res.items

                });
            }

        const onChangeInput = (evt, prop) => {
             let value
            switch (prop) {
                case 'atleta':
                value = evt.value
                break;
                
                default:
                value = evt.target.value
                break;
            }
            
            
            };
           
        onMounted(() => { 
            loading.value = true
            const promises = [fetchAtleti(), fetchActivities()]
            
            Promise.all(promises)
            .finally(()=>{
                loading.value = false
            })
        })
        
        watch(selectedActivity, () => {
             pageUsed.value = 1; // Reset to first page on filter change
             if (showAll.value) {
                 fetchData();
             }
        });

        return {
            getNomeCompleto,showCompleteButton,onCellEditComplete,confermaCancellazione,
            goToAtleta,showAllTextAlt,showAllText,changeData,showAll,quanti,onFileChange,onChangeInput,cancella,loading,transactions
            ,transactionsNotUsed,quantiNo,atleti,dapagare,confirmDialogRejectUsed,showModal,selectedAtleta,datiTransazione,
            pagamento,changeAtleta,viewSetPagamento,onRowSelect,sendPagamento,highlightNames,openConfirmDialogUsed,confirmDialogUsed,
            activities, selectedActivity, pageUsed, limitUsed, totalUsed, pageNoUsed, limitNoUsed, totalNoUsed, onPageChangeUsed, onPageChangeNoUsed,
            saveAndSendReceipt, canSendReceipt, previewVisible, previewData, confermaImport, annullaImport, fileInput, triggerFileInput, excludeUscite
        };
    },
   
}
</script>

<style scoped>
.modal {
  position: fixed;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-height: 90vh;
  overflow-y: auto;
  background: white;
  padding: 1em;
  border: 1px solid #ccc;
  width: 60vw;
}
.modal .p-datatable {
  max-height: 500px; /* Puoi cambiare l'altezza massima */
  overflow-y: auto;
}
</style>
